
import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAdsByPosition } from '@/utils/adService';

interface AdDisplayProps {
  position: 'header' | 'footer' | 'sidebar' | 'before-game' | 'after-game' | 'between-games' | 'blog-above-post' | 'blog-related-games';
  className?: string;
}

const AdDisplay: React.FC<AdDisplayProps> = ({ position, className = '' }) => {
  const { data: ads = [] } = useQuery({
    queryKey: ['ads', position],
    queryFn: () => getAdsByPosition(position),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    // Load AdSense script if not already loaded
    if (ads.length > 0 && !window.adsbygoogle) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
      document.head.appendChild(script);
      
      // Initialize the adsbygoogle array
      window.adsbygoogle = window.adsbygoogle || [];
    }
  }, [ads]);

  // Return null if no ads - this prevents empty containers from taking up space
  if (ads.length === 0) {
    return null;
  }

  // Special styling for native ads that should match game cards
  const isNativeAd = position === 'between-games' || position === 'blog-related-games';
  const isBlogAbovePost = position === 'blog-above-post';
  const isHeaderOrFooter = position === 'header' || position === 'footer';

  return (
    <div className={`ad-container ${className}`}>
      {ads.slice(0, 1).map((ad) => (
        <div 
          key={ad.id} 
          className={`ad-unit ${isNativeAd ? '' : 'mb-4'}`}
        >
          {isHeaderOrFooter ? (
            // Header/Footer banner ad styling - full width
            <div className={`w-full h-20 flex items-center justify-center text-white font-medium ${
              position === 'header' 
                ? 'bg-gradient-to-r from-purple-400 via-purple-500 to-purple-600' 
                : 'bg-gradient-to-r from-pink-400 via-orange-400 to-orange-500'
            }`}>
              {ad.content ? (
                <div 
                  className="w-full h-full flex items-center justify-center"
                  dangerouslySetInnerHTML={{ __html: ad.content }}
                />
              ) : (
                <div className="text-center">
                  <div className="text-lg font-semibold mb-1">Advertisement</div>
                  <div className="text-sm opacity-90">
                    {ad.name || (position === 'header' ? '728x90 Leaderboard Banner' : 'Footer Banner')}
                  </div>
                </div>
              )}
            </div>
          ) : isNativeAd ? (
            // Native ad in its own format - square container to match grid
            <div className="w-full aspect-square bg-white rounded-lg shadow-md overflow-hidden flex items-center justify-center">
              {ad.content ? (
                <div 
                  className="w-full h-full flex items-center justify-center"
                  dangerouslySetInnerHTML={{ __html: ad.content }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-blue-100">
                  <div className="text-center p-4">
                    <div className="w-16 h-16 bg-purple-200 rounded-full mx-auto mb-2 flex items-center justify-center">
                      <span className="text-purple-600 font-bold text-xl">Ad</span>
                    </div>
                    <p className="text-sm text-gray-600 font-medium">
                      {ad.name || 'Advertisement'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : isBlogAbovePost ? (
            // Blog post above content ad with special styling
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                  Advertisement
                </span>
                <span className="text-xs text-gray-400">Sponsored</span>
              </div>
              <div 
                className="ad-content"
                dangerouslySetInnerHTML={{ __html: ad.content || '' }}
              />
            </div>
          ) : (
            <div 
              dangerouslySetInnerHTML={{ __html: ad.content || '' }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default AdDisplay;
